import './src/utils/firebase-logger-fix'; // Import this first to fix logging
import './src/utils/env-checker'; // Check environment variables on startup
import './src/i18n'; // Initialize i18n
import './src/firebase/firebase'; // Initialize Firebase with new centralized config
import 'react-native-gesture-handler'; // Import gesture handler first
import './web-styles.css'; // Import web-specific styles
import React, { useEffect } from 'react';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { AuthProvider } from './src/context/auth-context';
import { OrganizationProvider } from './src/context/organization-context';
import { AlertProvider } from './src/hooks/use-custom-alert';
import { CustomAlertProvider } from './src/components/common/custom-alert-modal';
import { CustomPromptProvider } from './src/components/common/custom-prompt-modal';
import { DeletionProgressModal } from './src/components/common/deletion-progress-modal';
import AppNavigator from './src/navigators/app-navigator';
import { PWAInstallPrompt } from './src/components/pwa/pwa-install-prompt';

// Web-specific setup
const setupWebEnvironment = () => {
  // Set up web-specific viewport meta tag
  const viewportMeta = document.querySelector('meta[name="viewport"]');
  if (viewportMeta) {
    viewportMeta.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );
  } else {
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content =
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
    document.head.appendChild(meta);
  }

  // Add web-specific classes to body
  document.body.classList.add('web-app');

  // Fix for iOS Safari viewport height
  const setVH = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  setVH();
  window.addEventListener('resize', setVH);
  window.addEventListener('orientationchange', setVH);

  // Prevent zoom on input focus for iOS
  const preventZoom = (e: Event) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      e.target.style.fontSize = '16px';
    }
  };

  document.addEventListener('focusin', preventZoom);

  return () => {
    window.removeEventListener('resize', setVH);
    window.removeEventListener('orientationchange', setVH);
    document.removeEventListener('focusin', preventZoom);
  };
};

const App: React.FC = () => {
  useEffect(() => {
    const cleanup = setupWebEnvironment();
    return cleanup;
  }, []);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <StatusBar style="auto" />
        <AuthProvider>
          <OrganizationProvider>
            <AlertProvider>
              <CustomAlertProvider>
                <CustomPromptProvider>
                  <DeletionProgressModal>
                    <AppNavigator />
                    <PWAInstallPrompt />
                  </DeletionProgressModal>
                </CustomPromptProvider>
              </CustomAlertProvider>
            </AlertProvider>
          </OrganizationProvider>
        </AuthProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
};

export default App;
